import React, { useState } from "react";

import {
  Check,
  Users,
  ShoppingCart,
  BarChart3,
  Globe,
  Mail,
  Phone,
  ArrowRight,
  Building,
  User,
  Utensils,
  Package,
  TrendingUp,
  Shield,
  Clock,
  HeadphonesIcon,
} from "lucide-react";

const LandingPage = () => {
  const [selectedPlan, setSelectedPlan] = useState("profesional");
  const [showRegistration, setShowRegistration] = useState(false);
  const [formData, setFormData] = useState({
    restaurantName: "",
    ownerName: "",
    email: "",
    phone: "",
    address: "",
    city: "",
    country: "",
    restaurantType: "",
    paymentMethod: "credit_card",
  });

  const plans = [
    {
      id: "basico",
      name: "Básico",
      price: "$29",
      period: "/mes",
      description: "Perfecto para restaurantes pequeños que están comenzando",
      features: [
        "Hasta 5 mesas",
        "Menú digital básico",
        "Punto de venta simple",
        "Inventario básico",
        "Reportes básicos",
        "Soporte por email",
      ],
      color: "from-purple-400 to-purple-500",
      popular: false,
    },
    {
      id: "profesional",
      name: "Profesional",
      price: "$59",
      period: "/mes",
      description: "La opción más popular para restaurantes en crecimiento",
      features: [
        "Hasta 20 mesas",
        "Menú digital completo",
        "POS avanzado",
        "Inventario completo",
        "Gestión de empleados",
        "Reportes avanzados",
        "Página web incluida",
        "Email marketing",
        "Soporte prioritario",
      ],
      color: "from-purple-600 to-indigo-600",
      popular: true,
    },
    {
      id: "empresarial",
      name: "Empresarial",
      price: "$99",
      period: "/mes",
      description: "Para cadenas de restaurantes y franquicias",
      features: [
        "Mesas ilimitadas",
        "Multi-ubicación",
        "Gestión centralizada",
        "API completa",
        "Integraciones personalizadas",
        "Análisis avanzado",
        "Soporte 24/7",
        "Entrenamiento incluido",
        "Gerente de cuenta dedicado",
      ],
      color: "from-indigo-600 to-purple-700",
      popular: false,
    },
  ];

  const restaurantTypes = [
    "Restaurante casual",
    "Comida rápida",
    "Fine dining",
    "Cafetería",
    "Pizzería",
    "Panadería",
    "Bar/Pub",
    "Food truck",
    "Delivery only",
    "Otro",
  ];

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    console.log("Registration data:", { ...formData, plan: selectedPlan });
    alert(
      "¡Registro exitoso! Te contactaremos pronto para configurar tu instancia.",
    );
  };

  if (showRegistration) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-indigo-50">
        <div className="container mx-auto px-4 py-8">
          <div className="max-w-4xl mx-auto">
            {/* Header */}
            <div className="text-center mb-8">
              <h1 className="text-4xl font-bold bg-gradient-to-r from-purple-600 to-indigo-600 bg-clip-text text-transparent mb-4">
                Crear tu Instancia Byte
              </h1>
              <p className="text-xl text-gray-600">
                Solo unos datos más para configurar tu restaurante
              </p>
            </div>

            {/* Registration Form */}
            <div className="bg-white rounded-2xl shadow-xl p-8 border border-purple-100">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Restaurant Info */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                      <Building className="h-5 w-5 text-purple-600" />
                      Información del Restaurante
                    </h3>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Nombre del Restaurante *
                      </label>
                      <input
                        type="text"
                        name="restaurantName"
                        value={formData.restaurantName}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 border border-purple-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                        placeholder="Ej: Restaurante El Sabor"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Tipo de Restaurante *
                      </label>
                      <select
                        name="restaurantType"
                        value={formData.restaurantType}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 border border-purple-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                      >
                        <option value="">Selecciona una opción</option>
                        {restaurantTypes.map((type) => (
                          <option key={type} value={type}>
                            {type}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Dirección *
                      </label>
                      <input
                        type="text"
                        name="address"
                        value={formData.address}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 border border-purple-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                        placeholder="Calle 123, Colonia Centro"
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Ciudad *
                        </label>
                        <input
                          type="text"
                          name="city"
                          value={formData.city}
                          onChange={handleInputChange}
                          required
                          className="w-full px-4 py-3 border border-purple-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          País *
                        </label>
                        <input
                          type="text"
                          name="country"
                          value={formData.country}
                          onChange={handleInputChange}
                          required
                          className="w-full px-4 py-3 border border-purple-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Owner Info */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                      <User className="h-5 w-5 text-purple-600" />
                      Información del Propietario
                    </h3>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Nombre Completo *
                      </label>
                      <input
                        type="text"
                        name="ownerName"
                        value={formData.ownerName}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 border border-purple-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                        placeholder="Juan Pérez"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Email *
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 border border-purple-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                        placeholder="juan@email.com"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Teléfono *
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 border border-purple-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                        placeholder="+57 3006157405"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Método de Pago *
                      </label>
                      <select
                        name="paymentMethod"
                        value={formData.paymentMethod}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 border border-purple-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                      >
                        <option value="credit_card">Tarjeta de Crédito</option>
                        <option value="debit_card">Tarjeta de Débito</option>
                        <option value="bank_transfer">
                          Transferencia Bancaria
                        </option>
                        <option value="paypal">PayPal</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* Selected Plan Summary */}
                <div className="bg-gradient-to-r from-purple-50 to-indigo-50 rounded-xl p-6 border border-purple-200">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">
                    Plan Seleccionado
                  </h3>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xl font-bold text-purple-600">
                        Plan {plans.find((p) => p.id === selectedPlan)?.name}
                      </p>
                      <p className="text-gray-600">
                        {plans.find((p) => p.id === selectedPlan)?.description}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-3xl font-bold text-gray-900">
                        {plans.find((p) => p.id === selectedPlan)?.price}
                        <span className="text-lg text-gray-500">/mes</span>
                      </p>
                      <button
                        type="button"
                        onClick={() => setShowRegistration(false)}
                        className="text-purple-600 hover:text-purple-700 text-sm font-medium"
                      >
                        Cambiar plan
                      </button>
                    </div>
                  </div>
                </div>

                {/* Submit Button */}
                <div className="flex gap-4">
                  <button
                    type="button"
                    onClick={() => setShowRegistration(false)}
                    className="flex-1 px-6 py-3 border border-purple-200 text-purple-600 rounded-xl hover:bg-purple-50 transition-colors font-medium"
                  >
                    Regresar
                  </button>
                  <button
                    type="submit"
                    className="flex-1 px-6 py-3 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-xl hover:from-purple-700 hover:to-indigo-700 transition-all duration-300 font-medium flex items-center justify-center gap-2"
                  >
                    Crear mi Instancia
                    <ArrowRight className="h-5 w-5" />
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-indigo-50">
      {/* Hero Section */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-96 h-96 bg-purple-500 rounded-full -translate-x-48 -translate-y-48"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-indigo-500 rounded-full translate-x-48 translate-y-48"></div>
        </div>

        <div className="relative z-10 container mx-auto px-4 py-20">
          <div className="text-center mb-16">
            <h1 className="text-6xl font-bold bg-gradient-to-r from-purple-600 to-indigo-600 bg-clip-text text-transparent mb-2">
              Byte Restaurant
            </h1>
            <p className="text-sm text-purple-600 font-medium mb-6">
              Powered by Sentral-AI
            </p>
            <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
              La plataforma completa para gestionar tu restaurante. Desde el
              punto de venta hasta la gestión de inventarios, todo en un solo
              lugar con la potencia de Odoo y la simplicidad de Byte.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-full shadow-lg">
                <Shield className="h-5 w-5 text-green-500" />
                <span className="text-sm font-medium">100% Seguro</span>
              </div>
              <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-full shadow-lg">
                <Clock className="h-5 w-5 text-blue-500" />
                <span className="text-sm font-medium">
                  Configuración en 5 min
                </span>
              </div>
              <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-full shadow-lg">
                <HeadphonesIcon className="h-5 w-5 text-purple-500" />
                <span className="text-sm font-medium">Soporte 24/7</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Todo lo que necesitas para tu restaurante
            </h2>
            <p className="text-xl text-gray-600">
              Una solución completa que crece contigo
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                icon: ShoppingCart,
                title: "Punto de Venta",
                desc: "POS moderno y fácil de usar",
              },
              {
                icon: Utensils,
                title: "Menú Digital",
                desc: "Gestiona tu carta digitalmente",
              },
              {
                icon: Package,
                title: "Inventarios",
                desc: "Control total de tu stock",
              },
              {
                icon: BarChart3,
                title: "Reportes",
                desc: "Análisis y métricas avanzadas",
              },
              {
                icon: Users,
                title: "Empleados",
                desc: "Gestión completa de personal",
              },
              {
                icon: Globe,
                title: "Página Web",
                desc: "Presencia online profesional",
              },
              {
                icon: Mail,
                title: "Marketing",
                desc: "Campañas de email automáticas",
              },
              {
                icon: TrendingUp,
                title: "Ventas",
                desc: "Optimiza tus ingresos",
              },
            ].map((feature, idx) => (
              <div
                key={idx}
                className="text-center p-6 rounded-2xl hover:bg-gradient-to-br hover:from-purple-50 hover:to-indigo-50 transition-all duration-300 group"
              >
                <div className="bg-gradient-to-br from-purple-500 to-indigo-500 p-3 rounded-xl w-16 h-16 mx-auto mb-4 group-hover:scale-110 transition-transform">
                  <feature.icon className="h-10 w-10 text-white" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  {feature.title}
                </h3>
                <p className="text-gray-600">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Plans Section */}
      <section className="py-20 bg-gradient-to-br from-purple-50 to-indigo-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Elige el plan perfecto para tu restaurante
            </h2>
            <p className="text-xl text-gray-600">
              Precios transparentes, sin sorpresas
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {plans.map((plan) => (
              <div
                key={plan.id}
                className={`bg-white rounded-2xl shadow-xl overflow-hidden transform hover:scale-105 transition-all duration-300 ${
                  plan.popular ? "ring-4 ring-purple-500 ring-opacity-50" : ""
                } ${selectedPlan === plan.id ? "ring-4 ring-purple-600" : ""}`}
              >
                {plan.popular && (
                  <div className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white text-center py-2 font-medium">
                    Más Popular
                  </div>
                )}

                <div className="p-8">
                  <div className="text-center mb-6">
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">
                      {plan.name}
                    </h3>
                    <p className="text-gray-600 mb-4">{plan.description}</p>
                    <div className="mb-6">
                      <span className="text-5xl font-bold text-gray-900">
                        {plan.price}
                      </span>
                      <span className="text-gray-500">{plan.period}</span>
                    </div>
                  </div>

                  <ul className="space-y-3 mb-8">
                    {plan.features.map((feature, idx) => (
                      <li key={idx} className="flex items-center gap-3">
                        <Check className="h-5 w-5 text-green-500 flex-shrink-0" />
                        <span className="text-gray-700">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  <button
                    onClick={() => setSelectedPlan(plan.id)}
                    className={`w-full py-3 rounded-xl font-medium transition-all duration-300 ${
                      selectedPlan === plan.id
                        ? "bg-gradient-to-r from-purple-600 to-indigo-600 text-white"
                        : "border-2 border-purple-200 text-purple-600 hover:bg-purple-50"
                    }`}
                  >
                    {selectedPlan === plan.id
                      ? "Seleccionado"
                      : "Seleccionar Plan"}
                  </button>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <button
              onClick={() => setShowRegistration(true)}
              className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white px-8 py-4 rounded-xl hover:from-purple-700 hover:to-indigo-700 transition-all duration-300 font-medium text-lg flex items-center gap-2 mx-auto"
            >
              Comenzar con el Plan{" "}
              {plans.find((p) => p.id === selectedPlan)?.name}
              <ArrowRight className="h-5 w-5" />
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div>
              <h3 className="text-2xl font-bold bg-gradient-to-r from-purple-400 to-indigo-400 bg-clip-text text-transparent mb-2">
                Byte
              </h3>
              <p className="text-xs text-purple-400 font-medium mb-4">
                Powered by Sentral-AI
              </p>
              <p className="text-gray-400">
                Revolucionando la gestión de restaurantes con tecnología de
                vanguardia.
              </p>
            </div>
            <div>
              <h4 className="text-lg font-semibold mb-4">Producto</h4>
              <ul className="space-y-2 text-gray-400">
                <li>
                  <a href="#" className="hover:text-white transition-colors">
                    Características
                  </a>
                </li>
                <li>
                  <a href="#" className="hover:text-white transition-colors">
                    Precios
                  </a>
                </li>
                <li>
                  <a href="#" className="hover:text-white transition-colors">
                    Integraciones
                  </a>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="text-lg font-semibold mb-4">Soporte</h4>
              <ul className="space-y-2 text-gray-400">
                <li>
                  <a href="#" className="hover:text-white transition-colors">
                    Documentación
                  </a>
                </li>
                <li>
                  <a href="#" className="hover:text-white transition-colors">
                    Tutoriales
                  </a>
                </li>
                <li>
                  <a href="#" className="hover:text-white transition-colors">
                    Contacto
                  </a>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="text-lg font-semibold mb-4">Contacto</h4>
              <div className="space-y-2 text-gray-400">
                <div className="flex items-center gap-2">
                  <Mail className="h-4 w-4" />
                  <span>info@byte.com</span>
                </div>
                <div className="flex items-center gap-2">
                  <Phone className="h-4 w-4" />
                  <span>+57 300 6157405</span>
                </div>
              </div>
            </div>
          </div>
          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-400">
            <p>&copy; 2025 Byte Restaurant System. Powered by Sentral-AI</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
